import React, { useEffect, useRef } from 'react';
import { gsap, prefersReducedMotion } from './gsap';

type RevealProps = {
  children: React.ReactNode;
  className?: string;
  /** Balise HTML du wrapper */
  as?: keyof JSX.IntrinsicElements;
  /** Anime les enfants directs un par un (stagger) plutôt que le wrapper */
  stagger?: boolean | number;
  /** Décalage vertical de départ en px */
  y?: number;
  /** Échelle de départ (1 = pas de zoom) */
  scale?: number;
  delay?: number;
  duration?: number;
  /** Point de déclenchement ScrollTrigger (ex: "top 85%") */
  start?: string;
  id?: string;
  style?: React.CSSProperties;
};

/**
 * Wrapper d'apparition au scroll (GSAP + ScrollTrigger).
 * Les éléments sont cachés avant d'entrer dans le viewport puis révélés une seule fois.
 */
const Reveal: React.FC<RevealProps> = ({
  children,
  className,
  as = 'div',
  stagger = false,
  y = 40,
  scale = 1,
  delay = 0,
  duration = 0.9,
  start = 'top 85%',
  id,
  style,
}) => {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReducedMotion()) return;

    const targets: Element[] = stagger ? Array.from(el.children) : [el];
    if (targets.length === 0) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        targets,
        { autoAlpha: 0, y, scale },
        {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          duration,
          delay,
          ease: 'power3.out',
          stagger: stagger ? (typeof stagger === 'number' ? stagger : 0.1) : 0,
          clearProps: 'transform',
          scrollTrigger: {
            trigger: el,
            start,
            once: true,
          },
        },
      );
    });

    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Tag = as as any;
  return (
    <Tag ref={ref} className={className} id={id} style={style}>
      {children}
    </Tag>
  );
};

export default Reveal;
